import PropTypes from 'prop-types';
import React from 'react';
import EditIcon from '@material-ui/icons/Edit';
import ScheduleIcon from '@material-ui/icons/Schedule';
import CheckCircleOutline from '@material-ui/icons/CheckCircleOutline';
import {
  styleUtils,
  Tooltip,
  Button as BaseButton,
  theme,
} from '@ublend-npm/aulaui-next';
import { MaterialProgressStatus } from '@ublend-npm/aula-schema';
import { evaluateMaterialAction } from '@core/materials/permissions';
import ScheduledItemHeader from '../../../common/ScheduledItemHeader';

import {
  BUTTON_SAVE,
  BUTTON_PREVIEW,
  BUTTON_EXIT_PREVIEW,
  MATERIAL_EDIT,
  MATERIAL_UNHIDE_TOOLTIP,
  MATERIAL_SCHEDULE_TOOLTIP,
  MATERIAL_PUBLISH,
} from '../../../../../constants/texts';
import {
  Container,
  Button,
  ActionsContainer,
  PageDetailsContainer,
  IconButton,
  VisibilityOffIcon,
  VisibilityOnIcon,
  ButtonContent,
} from './ActionBar.styled';
import { StepCompleteButton } from './ReadonlyActionBar.syled';
import SectionViewActionMenu from './SectionViewActionMenu';

const renderHiddenPageInfo = () => (
  <PageDetailsContainer>
    <VisibilityOffIcon />
    This page is hidden
  </PageDetailsContainer>
);

const renderScheduledPageInfo = (scheduledFor, items, saving) => (
  <ScheduledItemHeader
    scheduledFor={scheduledFor}
    items={items}
    disabled={saving}
  />
);

const renderPageInfo = ({ hidden, scheduledFor, items, saving }) => {
  if (hidden && scheduledFor) {
    return renderScheduledPageInfo(new Date(scheduledFor), items, saving);
  }
  if (hidden) {
    return renderHiddenPageInfo();
  }
  return null;
};

const ActionBar = ({
  onUnhide,
  onSave,
  onEdit,
  onPreview,
  onExitPreview,
  onDeleteSection,
  onHideSection,
  onFindPreviousVersion,
  onImportMaterialPage,
  onExportMaterialPage,
  showUnhide,
  showSave,
  showEdit,
  showPreview,
  isPreviewing,
  canSave,
  canFindPreviousVersion,
  canExportMaterialPage,
  a11yMessage,
  saving,
  openScheduleDialog,
  onKeepHidden,
  scheduledFor,
  educatorOnly,
  canEditMaterial = true,
  canDeleteMaterial = true,
  permissions = [],
  isRbacEnabled = false,
  isStudent = false,
  isReadOnlyUser = false,
  sectionId,
  isComplete,
  isCompleteLoading,
  submitProgress,
  isSectionPublished = false,
}) => {
  const items = [
    {
      label: 'Change scheduled time',
      action: openScheduleDialog,
    },
    {
      label: 'Undo scheduling and keep hidden',
      action: onKeepHidden,
    },
    {
      label: 'Publish now',
      action: onUnhide,
    },
  ];

  // In educator area: if user has educator area permission (canEditMaterial is true), they can do everything
  // Skip all permission checks when in educator area
  const effectivePermissions = !isRbacEnabled ? [] : permissions;

  // If in educator area and user has permission, allow all actions
  const allowAllInEducatorArea = educatorOnly && canEditMaterial;

  const scheduleStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('SCHEDULE_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const editStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('EDIT_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });
  const publishStatus = allowAllInEducatorArea
    ? { allowed: true }
    : evaluateMaterialAction('PUBLISH_PAGE', {
        permissions: effectivePermissions,
        fallback: canEditMaterial,
      });

  // Mark as complete button for students
  const StepCompletedIcon = () => (
    <CheckCircleOutline
      htmlColor="white"
      data-testid="completed-icon"
      role="img"
      aria-label="Step completed"
    />
  );
  const MarkAsCompleteIcon = () => (
    <CheckCircleOutline
      htmlColor={theme.color.grey7}
      data-testid="mark-as-complete-icon"
      role="img"
      aria-label="Mark as complete"
    />
  );

  const renderMarkAsCompleteButton = () => {
    // Never show mark as complete in educator area
    if (educatorOnly) {
      return null;
    }

    // Don't show mark as complete for hidden (unpublished) pages (including if parent folder is hidden)
    if (!isSectionPublished) {
      return null;
    }

    if (!sectionId || submitProgress === undefined) {
      return null;
    }

    // Show only if user has progress permission
    // If permissions are available (even when RBAC is disabled), check for progress permission
    // Otherwise, fall back to legacy behavior (show for students when RBAC is disabled)
    const hasPermissionsAvailable =
      permissions && Array.isArray(permissions) && permissions.length > 0;
    const hasProgressPermission = hasPermissionsAvailable
      ? permissions.includes('material:progress')
      : false;
    // Show if: user has progress permission OR (RBAC disabled AND no permissions available AND user is student)
    const shouldShowProgressButton = isRbacEnabled
      ? hasProgressPermission
      : isStudent;

    if (!shouldShowProgressButton) {
      return null;
    }

    return isComplete ? (
      <StepCompleteButton
        iconLeft={StepCompletedIcon}
        loading={isCompleteLoading}
        disabled={isCompleteLoading}
        size="medium"
        onClick={() => submitProgress(MaterialProgressStatus.INCOMPLETE)}
      >
        Step complete
      </StepCompleteButton>
    ) : (
      <BaseButton
        iconLeft={MarkAsCompleteIcon}
        loading={isCompleteLoading}
        disabled={isCompleteLoading}
        type="secondary"
        size="small"
        onClick={() => submitProgress(MaterialProgressStatus.COMPLETE)}
      >
        Mark as complete
      </BaseButton>
    );
  };

  return (
    <Container scheduledFor={scheduledFor} hidden={showUnhide}>
      {renderPageInfo({ hidden: showUnhide, scheduledFor, items })}
      <ActionsContainer>
        {/* For students: only show if they have permission (hide instead of disabled) */}
        {/* For staff users: hide completely */}
        {/* For instructors: show disabled with tooltip */}
        {showUnhide &&
          !scheduledFor &&
          !educatorOnly &&
          !isReadOnlyUser &&
          (isStudent && isRbacEnabled ? scheduleStatus.allowed : true) && (
            <Tooltip
              title={
                scheduleStatus.allowed
                  ? MATERIAL_SCHEDULE_TOOLTIP
                  : scheduleStatus.message
              }
              position="top"
            >
              <div>
                <IconButton
                  onClick={openScheduleDialog}
                  label="schedule"
                  size="small"
                  icon={ScheduleIcon}
                  disabled={!scheduleStatus.allowed}
                />
              </div>
            </Tooltip>
          )}
        {showEdit &&
          !isReadOnlyUser &&
          (isStudent && isRbacEnabled ? editStatus.allowed : true) && (
            <Tooltip
              title={editStatus.allowed ? MATERIAL_EDIT : editStatus.message}
              position="top"
            >
              <div>
                <IconButton
                  type="secondary"
                  onClick={onEdit}
                  label={MATERIAL_EDIT}
                  size="small"
                  icon={EditIcon}
                  disabled={!editStatus.allowed}
                />
              </div>
            </Tooltip>
          )}
        {showPreview &&
          !isPreviewing &&
          !isReadOnlyUser &&
          (isStudent && isRbacEnabled ? editStatus.allowed : true) && (
            <Button
              type="secondary"
              size="small"
              onClick={onPreview}
              show={showPreview}
            >
              <ButtonContent>
                <VisibilityOnIcon />
                {BUTTON_PREVIEW}
              </ButtonContent>
            </Button>
          )}
        {isPreviewing && (
          <Button
            type="secondary"
            size="small"
            onClick={onExitPreview}
            show={isPreviewing}
          >
            <ButtonContent>
              <VisibilityOnIcon />
              {BUTTON_EXIT_PREVIEW}
            </ButtonContent>
          </Button>
        )}
        {showUnhide &&
          !scheduledFor &&
          !isReadOnlyUser &&
          (isStudent && isRbacEnabled ? publishStatus.allowed : true) && (
            <Tooltip
              title={
                publishStatus.allowed
                  ? MATERIAL_UNHIDE_TOOLTIP
                  : publishStatus.message
              }
              position="bottom"
            >
              <div>
                <Button
                  type="secondary"
                  size="small"
                  id="material-publish-button"
                  onClick={onUnhide}
                  show={showUnhide}
                  disabled={!publishStatus.allowed}
                >
                  {MATERIAL_PUBLISH}
                </Button>
              </div>
            </Tooltip>
          )}

        <Button
          type="primary"
          size="small"
          data-testid="material-save-button"
          aria-label="Save material"
          id="material-save-button"
          onClick={onSave}
          show={showSave}
          loading={saving}
          disabled={saving || !canSave}
        >
          {BUTTON_SAVE}
        </Button>
        <p css={[styleUtils.visuallyHidden()]} aria-live="polite">
          {a11yMessage}
        </p>
        {((isStudent && isRbacEnabled) || !isReadOnlyUser) && (
          <SectionViewActionMenu
            educatorOnly={educatorOnly}
            canFindPreviousVersion={canFindPreviousVersion}
            canExportMaterialPage={canExportMaterialPage}
            onDeleteSection={onDeleteSection}
            onHideSection={
              !showUnhide && publishStatus.allowed ? onHideSection : null
            }
            onFindPreviousVersion={onFindPreviousVersion}
            onExportMaterialPage={onExportMaterialPage}
            onImportMaterialPage={onImportMaterialPage}
            editing={showEdit}
            canEditMaterial={canEditMaterial}
            canDeleteMaterial={canDeleteMaterial}
            permissions={permissions}
            isRbacEnabled={isRbacEnabled}
            isStudent={isStudent}
          />
        )}
        {renderMarkAsCompleteButton()}
      </ActionsContainer>
    </Container>
  );
};

ActionBar.propTypes = {
  ...SectionViewActionMenu.propTypes,
  scheduledFor: PropTypes.string,
  canSave: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  showUnhide: PropTypes.bool.isRequired,
  showSave: PropTypes.bool.isRequired,
  showEdit: PropTypes.bool.isRequired,
  showPreview: PropTypes.bool,
  isPreviewing: PropTypes.bool,
  a11yMessage: PropTypes.string,
  onUnhide: PropTypes.func,
  onSave: PropTypes.func,
  onEdit: PropTypes.func,
  onPreview: PropTypes.func,
  onExitPreview: PropTypes.func,
  onExportMaterialPage: PropTypes.func,
  onImportMaterialPage: PropTypes.func,
  educatorOnly: PropTypes.bool,
  openScheduleDialog: PropTypes.func,
  onKeepHidden: PropTypes.func,
  canEditMaterial: PropTypes.bool,
  canDeleteMaterial: PropTypes.bool,
  permissions: PropTypes.arrayOf(PropTypes.string),
  isStudent: PropTypes.bool,
  sectionId: PropTypes.string,
  isComplete: PropTypes.bool,
  isCompleteLoading: PropTypes.bool,
  submitProgress: PropTypes.func,
  isSectionPublished: PropTypes.bool,
};

ActionBar.defaultProps = {
  ...SectionViewActionMenu.defaultProps,
  onUnhide: () => null,
  onSave: () => null,
  onEdit: () => null,
  onPreview: () => null,
  onExitPreview: () => null,
  onKeepHidden: () => null,
  openScheduleDialog: () => null,
  scheduledFor: null,
  showPreview: false,
  isPreviewing: false,
  canEditMaterial: true,
  canDeleteMaterial: true,
  permissions: [],
};

export default ActionBar;
